import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabase";
import { ICON_OPTIONS, GoalIcon } from "../components/GoalCard";

export default function Goals() {
  const [settings, setSettings] = useState(null);
  const [players, setPlayers] = useState([]);
  const [teamName, setTeamName] = useState("");
  const [teamGoal, setTeamGoal] = useState("");
  const [endDate, setEndDate] = useState("");
  const [fundraiserClosed, setFundraiserClosed] = useState(false);
  const [displayMode, setDisplayMode] = useState("team"); // "team" or "individual"
  const [notificationEmails, setNotificationEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [goalCategories, setGoalCategories] = useState([]);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [catForm, setCatForm] = useState({ title: "", amount: "", description: "", icon: "equipment", sort_order: 0 });
  const [savingCat, setSavingCat] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");

  const fetchData = useCallback(async () => {
    const [settingsRes, playersRes, catRes] = await Promise.all([
      supabase.from("coach_settings").select("*").limit(1).single(),
      supabase.from("players").select("*").order("jersey_number"),
      supabase.from("goal_categories").select("*").order("sort_order"),
    ]);
    if (settingsRes.data) {
      setSettings(settingsRes.data);
      setTeamName(settingsRes.data.team_name || "");
      setTeamGoal(settingsRes.data.team_goal);
      setEndDate(settingsRes.data.end_date || "");
      setFundraiserClosed(settingsRes.data.fundraiser_closed || false);
      setDisplayMode(settingsRes.data.display_mode || "team");
      setNotificationEmails(settingsRes.data.notification_emails || []);
    }
    if (playersRes.data) setPlayers(playersRes.data);
    if (catRes.data) setGoalCategories(catRes.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveTeamSettings = async () => {
    setSaving(true);
    const updates = {
      team_name: teamName || "Buchtel Griffins Baseball Program",
      team_goal: Number(teamGoal),
      end_date: endDate || null,
    };
    if (settings) {
      await supabase
        .from("coach_settings")
        .update(updates)
        .eq("id", settings.id);
    } else {
      await supabase.from("coach_settings").insert({
        ...updates,
        team_name: "Buchtel Griffins Baseball Program",
        season_year: 2026,
      });
    }
    setSaving(false);
    setSavedMsg("Settings saved!");
    setTimeout(() => setSavedMsg(""), 2000);
    fetchData();
  };

  const toggleFundraiserClosed = async () => {
    const newValue = !fundraiserClosed;
    setFundraiserClosed(newValue);
    if (settings) {
      await supabase
        .from("coach_settings")
        .update({ fundraiser_closed: newValue })
        .eq("id", settings.id);
    }
  };

  const toggleDisplayMode = async () => {
    const newMode = displayMode === "team" ? "individual" : "team";
    setDisplayMode(newMode);
    if (settings) {
      await supabase
        .from("coach_settings")
        .update({ display_mode: newMode })
        .eq("id", settings.id);
    }
  };

  const addNotificationEmail = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || notificationEmails.includes(email)) {
      setNewEmail("");
      return;
    }
    const updated = [...notificationEmails, email];
    setNotificationEmails(updated);
    setNewEmail("");
    if (settings) {
      await supabase
        .from("coach_settings")
        .update({ notification_emails: updated })
        .eq("id", settings.id);
    }
  };

  const removeNotificationEmail = async (email) => {
    const updated = notificationEmails.filter((e) => e !== email);
    setNotificationEmails(updated);
    if (settings) {
      await supabase
        .from("coach_settings")
        .update({ notification_emails: updated })
        .eq("id", settings.id);
    }
  };

  const openAddCategory = () => {
    setEditingCategory(null);
    setCatForm({ title: "", amount: "", description: "", icon: "equipment", sort_order: goalCategories.length });
    setShowCategoryForm(true);
  };

  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setCatForm({
      title: cat.title,
      amount: cat.amount,
      description: cat.description,
      icon: cat.icon,
      sort_order: cat.sort_order,
    });
    setShowCategoryForm(true);
  };

  const saveCategory = async () => {
    setSavingCat(true);
    const payload = {
      title: catForm.title,
      amount: Number(catForm.amount),
      description: catForm.description,
      icon: catForm.icon,
      sort_order: Number(catForm.sort_order),
    };

    if (editingCategory) {
      await supabase.from("goal_categories").update(payload).eq("id", editingCategory.id);
    } else {
      await supabase.from("goal_categories").insert(payload);
    }

    setSavingCat(false);
    setShowCategoryForm(false);
    fetchData();
  };

  const deleteCategory = async (id) => {
    await supabase.from("goal_categories").delete().eq("id", id);
    fetchData();
  };

  const updatePlayerGoal = async (playerId, newGoal) => {
    await supabase
      .from("players")
      .update({ goal_amount: Number(newGoal) })
      .eq("id", playerId);
    fetchData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-navy/40">Loading goals...</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
      <h1 className="font-display text-2xl font-bold text-navy mb-6">Goals</h1>

      {/* Team Settings */}
      <div className="bg-white rounded-xl border border-warm-gray p-5 mb-6">
        <h2 className="font-semibold text-navy mb-3">Team Settings</h2>
        <div className="mb-4">
          <label className="block text-sm text-navy/60 mb-1">
            Team Name
          </label>
          <input
            type="text"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            placeholder="Buchtel Griffins Baseball Program"
            className="w-full px-4 py-2.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm text-navy/60 mb-1">
              Fundraising Goal ($)
            </label>
            <input
              type="number"
              min="0"
              value={teamGoal}
              onChange={(e) => setTeamGoal(e.target.value)}
              className="w-full px-4 py-2.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
            />
          </div>
          <div>
            <label className="block text-sm text-navy/60 mb-1">
              Fundraiser End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={saveTeamSettings}
            disabled={saving}
            className="bg-black hover:bg-neutral-800 text-white font-semibold text-sm py-2.5 px-6 rounded-lg transition-colors disabled:opacity-50 shrink-0"
          >
            {saving ? "Saving..." : "Save Settings"}
          </button>
          {savedMsg && (
            <span className="text-sm text-green-600">{savedMsg}</span>
          )}
        </div>
      </div>

      {/* Display Mode Toggle */}
      <div className="bg-white rounded-xl border border-warm-gray p-5 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-navy">Fundraiser Mode</h2>
            <p className="text-sm text-navy/50 mt-0.5">
              {displayMode === "team"
                ? "Team Spirit — only the team goal is shown publicly. Individual stats are tracked in your dashboard."
                : "Player Spotlight — individual player goals and progress are visible to donors."}
            </p>
          </div>
          <button
            onClick={toggleDisplayMode}
            className="shrink-0 ml-4 flex items-center gap-2 bg-cream border border-warm-gray rounded-full p-1 transition-colors"
          >
            <span
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                displayMode === "team"
                  ? "bg-navy text-white"
                  : "text-navy/40"
              }`}
            >
              Team Spirit
            </span>
            <span
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                displayMode === "individual"
                  ? "bg-navy text-white"
                  : "text-navy/40"
              }`}
            >
              Player Spotlight
            </span>
          </button>
        </div>
      </div>

      {/* Close Fundraiser Toggle */}
      <div className={`rounded-xl border p-5 mb-6 ${fundraiserClosed ? "bg-red-50 border-red-200" : "bg-white border-warm-gray"}`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-navy">Close Fundraiser Early</h2>
            <p className="text-sm text-navy/50 mt-0.5">
              {fundraiserClosed
                ? "Donations are currently disabled sitewide."
                : "This will disable all donation buttons on the site."}
            </p>
          </div>
          <button
            onClick={toggleFundraiserClosed}
            className={`relative w-12 h-7 rounded-full transition-colors ${
              fundraiserClosed ? "bg-red-500" : "bg-warm-gray"
            }`}
          >
            <div
              className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${
                fundraiserClosed ? "translate-x-[22px]" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Donation Notifications */}
      <div className="bg-white rounded-xl border border-warm-gray p-5 mb-6">
        <h2 className="font-semibold text-navy mb-1">Donation Notifications</h2>
        <p className="text-sm text-navy/50 mb-4">
          These people get an email every time someone donates.
        </p>

        {/* Existing emails */}
        {notificationEmails.length > 0 && (
          <div className="space-y-2 mb-4">
            {notificationEmails.map((email) => (
              <div
                key={email}
                className="flex items-center justify-between bg-cream rounded-lg px-4 py-2.5"
              >
                <span className="text-sm text-navy">{email}</span>
                <button
                  onClick={() => removeNotificationEmail(email)}
                  className="text-xs text-red-500 hover:text-red-700 font-medium"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add new email */}
        <div className="flex gap-2">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addNotificationEmail())}
            placeholder="email@example.com"
            className="flex-1 px-4 py-2.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
          />
          <button
            onClick={addNotificationEmail}
            disabled={!newEmail.trim()}
            className="bg-navy text-white text-sm font-semibold py-2.5 px-5 rounded-lg hover:bg-navy-light transition-colors disabled:opacity-40"
          >
            Add
          </button>
        </div>
      </div>

      {/* Goal Categories — "Where Your Money Goes" */}
      <div className="bg-white rounded-xl border border-warm-gray p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-navy">Where Your Money Goes</h2>
          <button
            onClick={openAddCategory}
            className="text-sm font-semibold text-black hover:text-neutral-600"
          >
            + Add Category
          </button>
        </div>

        {goalCategories.length === 0 && !showCategoryForm && (
          <p className="text-sm text-navy/40 text-center py-4">
            No categories yet. Add one to show donors where their money goes.
          </p>
        )}

        <div className="space-y-3">
          {goalCategories.map((cat) => (
            <div key={cat.id} className="flex items-center gap-3 p-3 bg-cream rounded-lg">
              <div className="text-navy shrink-0">
                <GoalIcon icon={cat.icon} className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-sm text-navy block">{cat.title}</span>
                <span className="text-xs text-navy/50">${cat.amount.toLocaleString()} — {cat.description}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => openEditCategory(cat)}
                  className="text-xs text-navy/50 hover:text-navy"
                >
                  Edit
                </button>
                <button
                  onClick={() => deleteCategory(cat.id)}
                  className="text-xs text-red-400 hover:text-red-600"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Add/Edit Category Form */}
        {showCategoryForm && (
          <div className="mt-4 p-4 bg-cream rounded-lg border border-warm-gray">
            <h3 className="font-semibold text-sm text-navy mb-3">
              {editingCategory ? "Edit Category" : "Add Category"}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs text-navy/60 mb-1">Title</label>
                <input
                  type="text"
                  value={catForm.title}
                  onChange={(e) => setCatForm({ ...catForm, title: e.target.value })}
                  placeholder="e.g. Coaching Equipment"
                  className="w-full px-3 py-2 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                />
              </div>
              <div>
                <label className="block text-xs text-navy/60 mb-1">Amount ($)</label>
                <input
                  type="number"
                  min="0"
                  value={catForm.amount}
                  onChange={(e) => setCatForm({ ...catForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-xs text-navy/60 mb-1">Description</label>
              <input
                type="text"
                value={catForm.description}
                onChange={(e) => setCatForm({ ...catForm, description: e.target.value })}
                placeholder="Short description for donors"
                className="w-full px-3 py-2 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
              />
            </div>
            <div className="mb-4">
              <label className="block text-xs text-navy/60 mb-1">Icon</label>
              <div className="flex gap-2 flex-wrap">
                {ICON_OPTIONS.map((key) => (
                  <button
                    key={key}
                    onClick={() => setCatForm({ ...catForm, icon: key })}
                    className={`p-2 rounded-lg border transition-colors ${
                      catForm.icon === key
                        ? "border-black bg-navy text-white"
                        : "border-warm-gray bg-white text-navy/60 hover:border-navy/30"
                    }`}
                    title={key}
                  >
                    <GoalIcon icon={key} className="w-5 h-5" />
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={saveCategory}
                disabled={savingCat || !catForm.title || !catForm.amount}
                className="bg-black hover:bg-neutral-800 text-white font-semibold text-sm py-2 px-5 rounded-lg transition-colors disabled:opacity-50"
              >
                {savingCat ? "Saving..." : editingCategory ? "Update" : "Add"}
              </button>
              <button
                onClick={() => setShowCategoryForm(false)}
                className="text-sm text-navy/50 hover:text-navy py-2 px-4"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Individual Player Goals */}
      <div className="bg-white rounded-xl border border-warm-gray overflow-hidden">
        <div className="px-5 py-3 border-b border-warm-gray">
          <h2 className="font-semibold text-navy text-sm">
            Individual Player Goals
          </h2>
        </div>
        <div className="divide-y divide-warm-gray">
          {players.map((p) => (
            <PlayerGoalRow
              key={p.id}
              player={p}
              onSave={updatePlayerGoal}
            />
          ))}
          {players.length === 0 && (
            <div className="px-5 py-8 text-center text-navy/40 text-sm">
              No players yet. Add players first.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PlayerGoalRow({ player, onSave }) {
  const [goal, setGoal] = useState(player.goal_amount);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const changed = Number(goal) !== player.goal_amount;

  const save = async () => {
    setSaving(true);
    await onSave(player.id, goal);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="flex items-center gap-4 px-5 py-3">
      <div className="flex-1 min-w-0">
        <span className="font-medium text-navy text-sm">{player.name}</span>
        <span className="text-navy/40 text-xs ml-2">#{player.jersey_number}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-navy/40 text-sm">$</span>
        <input
          type="number"
          min="0"
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          className="w-24 px-3 py-1.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black text-right"
        />
        {changed && (
          <button
            onClick={save}
            disabled={saving}
            className="text-xs font-semibold text-black hover:text-neutral-600 disabled:opacity-50"
          >
            {saving ? "..." : "Save"}
          </button>
        )}
        {saved && (
          <span className="text-xs text-green-600">Saved</span>
        )}
      </div>
    </div>
  );
}
